import { ChevronRight } from '@untitledui/icons';
import type {
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from 'react';
import { useCallback } from 'react';

import { ListCard } from '@/lib/base/list-card';
import { cx } from '@/utils/cx';

export interface ButtonListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * ButtonList — a list of clickable action rows.
 *
 * It composes `ListCard` for the card shell + dividers and adds
 * `ButtonList.Item` for the interactive rows. Use it for single-column lists
 * where each row is a discrete action (settings lists, pickers, simple
 * selectors). For a list-in-a-card of non-interactive content, use `ListCard`
 * directly.
 *
 * This is deliberately NOT a table: it has no column model, header, or cell
 * grid. Reach for `Table` only when you have genuinely tabular data with
 * columns; reach for `ButtonList`/`ListCard` for a list of rows.
 */
function ButtonListRoot({ children, ...props }: ButtonListProps) {
  return (
    <ListCard {...props} data-component='button-list'>
      {children}
    </ListCard>
  );
}
ButtonListRoot.displayName = 'ButtonList';

export interface ButtonListItemProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onClick' | 'children'
> {
  /** Primary (left) content of the row. */
  children: ReactNode;
  /**
   * Invoked when the row is activated by click or Enter/Space. Omit for a
   * static, non-interactive row.
   */
  onAction?: () => void;
  /** Disables row activation and dims the row. */
  isDisabled?: boolean;
  /**
   * Right-aligned trailing content (e.g. a toggle or menu). Clicks inside it
   * are isolated from the row action, so interacting with it does not also
   * fire `onAction`.
   */
  trailing?: ReactNode;
  /**
   * Render a trailing chevron that signifies the row opens a detail view /
   * drills in. Unlike `trailing`, the chevron is part of the row's click
   * target (clicking it fires `onAction`) and only renders when the row is
   * interactive. Use it for rows that navigate or open a panel — it makes the
   * affordance obvious rather than relying on the hover state alone.
   */
  showChevron?: boolean;
}

/**
 * A single row within a `ButtonList`. The whole row is the action target; put
 * any always-interactive control (toggle, menu button) in `trailing` so
 * interacting with it does not also trigger the row's `onAction`.
 */
function ButtonListItem({
  children,
  onAction,
  isDisabled = false,
  trailing,
  showChevron = false,
  className,
  ...props
}: ButtonListItemProps) {
  const interactive = !!onAction && !isDisabled;

  const handleClick = useCallback(() => {
    onAction?.();
  }, [onAction]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (
        !e.repeat &&
        (e.key === 'Enter' || e.key === ' ') &&
        e.currentTarget === e.target
      ) {
        e.preventDefault();
        onAction?.();
      }
    },
    [onAction]
  );

  const stopRowAction = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      {...props}
      data-component='button-list-item'
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-disabled={isDisabled || undefined}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      className={cx(
        'group flex items-center gap-4 px-4 py-3 transition-colors',
        interactive && 'cursor-pointer hover:bg-bg-secondary',
        isDisabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <div className='min-w-0 flex-1'>{children}</div>
      {trailing != null && (
        <div className='flex shrink-0 items-center' onClick={stopRowAction}>
          {trailing}
        </div>
      )}
      {showChevron && interactive && (
        <ChevronRight
          aria-hidden='true'
          className='-ml-1 size-4 shrink-0 text-fg-quaternary transition-colors group-hover:text-fg-tertiary'
        />
      )}
    </div>
  );
}
ButtonListItem.displayName = 'ButtonList.Item';

const ButtonList = ButtonListRoot as typeof ButtonListRoot & {
  Item: typeof ButtonListItem;
};
ButtonList.Item = ButtonListItem;

export { ButtonList };
